import { Box, useColorMode } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { ReactNode, useEffect, useRef, useState } from "react";
import { FaChevronUp } from "react-icons/fa";

interface AppScrollContainerProps {
  children: ReactNode;
}

export default function AppScrollContainer({
  children,
}: AppScrollContainerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { colorMode } = useColorMode();
  const router = useRouter();

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => setShowScrollTop(el.scrollTop > 100);
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleRouteChange = () => {
      scrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
    };
    router.events.on("routeChangeStart", handleRouteChange);
    return () => router.events.off("routeChangeStart", handleRouteChange);
  }, [router.events]);

  return (
    <Box position="fixed" inset={0} h="100dvh" overflow="hidden">
      <Box
        ref={scrollRef}
        as="main"
        h="100%"
        overflowY="auto"
        overscrollBehavior="none"
        sx={{
          "::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {children}
      </Box>
      {showScrollTop && (
        <Box
          onClick={() =>
            scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" })
          }
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          position="fixed"
          right="20px"
          bottom="80px"
          zIndex={1000}
          cursor="pointer"
          overflow={"hidden"}
          p="12px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderStyle="dotted"
          borderColor={colorMode === "dark" ? "white" : "black"}
          borderWidth={1}
          borderRadius="full"
          backdropFilter="auto"
          backdropBlur="8px"
          pointerEvents="auto" // ensure button receives events
        >
          <FaChevronUp
            size={24}
            color={colorMode === "dark" ? "white" : "black"}
          />
        </Box>
      )}
    </Box>
  );
}
